import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '../../context/ThemeContext';
import { useUnifiedAuth } from '../../context/UnifiedAuthContext';
import { submitBugReport, collectSystemInfo } from '../../services/bugReportService';
import { Modal, Button, AlertBanner } from '../ui';
import { Logger } from '../../utils/logger';

const log = Logger.create('BugReportModal');

/**
 * BugReportModal - Modal für Bug-Reports
 * 
 * Features:
 * - Theme-abhängiges Design
 * - Erfasst automatisch System-Informationen
 * - Speichert in Firestore
 * - Optional: Console Logs erfassen
 */
export function BugReportModal({ isOpen, onClose }) {
  const { isDark } = useTheme();
  const { currentUser } = useUnifiedAuth();
  
  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('functionality');
  const [stepsToReproduce, setStepsToReproduce] = useState('');
  
  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setTitle('');
      setDescription('');
      setCategory('functionality');
      setStepsToReproduce('');
      setError('');
      setSuccess(false);
    }
  }, [isOpen]);

  // Capture recent console logs (optional)
  const captureConsoleLogs = () => {
    // This would require implementing console interceptor
    // For now, return empty array
    return [];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validation
    if (!title.trim()) {
      setError('Bitte gib einen Titel ein');
      return;
    }
    if (!description.trim()) {
      setError('Bitte beschreibe das Problem');
      return;
    }

    setIsSubmitting(true);

    try {
      const systemInfo = collectSystemInfo(isDark);
      const consoleLogs = captureConsoleLogs();

      const bugReport = {
        // User inputs
        title: title.trim(),
        description: description.trim(),
        category,
        stepsToReproduce: stepsToReproduce.trim(),
        
        // User info
        userId: currentUser?.uid || null,
        userEmail: currentUser?.email || null,
        userRole: currentUser?.role || null,
        
        // System info
        ...systemInfo,
        consoleLogs,
        
        // Status
        status: 'new',
        priority: 'medium'
      };

      await submitBugReport(bugReport);
      
      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000);

    } catch (err) {
      log.error('Bug report submission error', err);
      setError('Fehler beim Senden. Bitte versuche es erneut.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Bug melden"
      size="lg"
      closeOnBackdrop={!isSubmitting}
      closeOnEscape={!isSubmitting}
    >
      {success ? (
        <div className="text-center py-8">
          <svg className="w-24 h-24 mx-auto mb-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Vielen Dank!
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Dein Bug-Report wurde erfolgreich gesendet.
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <AlertBanner
              variant="danger"
              message={error}
              onDismiss={() => setError('')}
            />
          )}

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Kategorie
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                       bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                       focus:ring-2 focus:ring-blue-500 focus:border-transparent
                       disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting}
            >
              <option value="ui">UI/Design Problem</option>
              <option value="functionality">Funktionalität funktioniert nicht</option>
              <option value="performance">Performance Problem</option>
              <option value="data">Daten/Speicherung</option>
              <option value="other">Sonstiges</option>
            </select>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Titel <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Kurze Beschreibung des Problems"
              maxLength={100}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                       bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                       placeholder-gray-500 dark:placeholder-gray-400
                       focus:ring-2 focus:ring-blue-500 focus:border-transparent
                       disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting}
              required
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {title.length}/100 Zeichen
            </p>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Beschreibung <span className="text-red-500">*</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Was ist passiert? Was hast du erwartet?"
              rows={4}
              maxLength={1000}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                       bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                       placeholder-gray-500 dark:placeholder-gray-400
                       focus:ring-2 focus:ring-blue-500 focus:border-transparent
                       disabled:opacity-50 disabled:cursor-not-allowed resize-none"
              disabled={isSubmitting}
              required
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {description.length}/1000 Zeichen
            </p>
          </div>

          {/* Steps to Reproduce */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Schritte zur Reproduktion (Optional)
            </label>
            <textarea
              value={stepsToReproduce}
              onChange={(e) => setStepsToReproduce(e.target.value)}
              placeholder="1. Gehe zu...&#10;2. Klicke auf...&#10;3. Dann passiert..."
              rows={3}
              maxLength={500}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                       bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                       placeholder-gray-500 dark:placeholder-gray-400
                       focus:ring-2 focus:ring-blue-500 focus:border-transparent
                       disabled:opacity-50 disabled:cursor-not-allowed resize-none"
              disabled={isSubmitting}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {stepsToReproduce.length}/500 Zeichen
            </p>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
            <p className="text-sm text-blue-800 dark:text-blue-300 flex items-start">
              <svg className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>
                <strong>Hinweis:</strong> Technische Informationen (Browser, System, URL, etc.) 
                werden automatisch erfasst und mitgesendet.
              </span>
            </p>
          </div>

          {/* Footer Buttons */}
          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="secondary"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Abbrechen
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 inline" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Wird gesendet...
                </>
              ) : (
                'Bug melden'
              )}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}

BugReportModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default BugReportModal;
